import type { Context } from './asyncHelpers'
import { fixStack, getDuration } from './asyncHelpers'
import { DefaultError } from './DefaultError'
import { Level } from './Level'

export type Request = {
	statusCode: number
	url: string
	method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
	/** Normalized URL of the request, with dynamic parameters replaced with placeholders. */
	route: string
	/** Request duration in seconds */
	duration: number
	/** Data sent for the request. Generally only included in development. */
	data: unknown
	responseData: unknown
}

type InitialRequest = Pick<Request, 'url' | 'method' | 'data'> & { correlationId: string }

// Use pretty route from API or attempt to create one from the URL by stripping IDs
const getRoute = (routeHeader: string | null | undefined, url: string) => {
	if (routeHeader) {
		return routeHeader
	}

	return (
		url
			// Remove guids
			.replace(/([^/])\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}(?=$|\/|\?|#)/gi, '$1/:guid')
			// Remove integers
			.replace(/([^/])\/\d+(?=$|\/|\?|#)/g, '$1/:integer')
			// Remove segments containing numbers, dashes or underscores or consisting of all uppercase
			// This should not be used for ordinary routing
			.replace(/([^/])\/(?!v\d+\/)(?:[^/?#]*[0-9-_][^/?#]*|[A-Z]+)(?=$|\/|\?|#)/g, '$1/:string')
	)
}

const getLevel = (statusCode: number) => {
	if (statusCode > 0 && statusCode < 500) {
		return Level.Warning
	}
	return Level.Error
}

export class RequestError extends DefaultError {
	public request: Request
	public correlationId: string

	protected constructor(message: string, correlationId: string, request: Request, data?: Record<string, any>) {
		super(message, { ...data, correlationId, request }, getLevel(request.statusCode))

		this.request = request
		this.correlationId = correlationId
	}

	/** Create from a response with an error status-code */
	public static fromResponse(
		{ status, statusText, headers }: any,
		responseData: any,
		{ data: { correlationId, ...reqeustData }, stackReference, startTime }: Context<InitialRequest>,
	) {
		const route = getRoute(headers.get('X-Route'), reqeustData.url)
		return fixStack(
			stackReference,
			new RequestError(`${reqeustData.method} ${route} responded ${status} ${statusText}`, correlationId, {
				...reqeustData,
				route,
				duration: getDuration(startTime),
				statusCode: status,
				responseData,
			}),
		)
	}

	/** Create from an error thrown by the request */
	public static fromError(
		error: Error,
		{ data: { correlationId, ...requestData }, stackReference, startTime }: Context<InitialRequest>,
	) {
		const route = getRoute(null, requestData.url)
		return fixStack(
			stackReference,
			new RequestError(`${requestData.method} ${route} failed with ${error.message}`, correlationId, {
				...requestData,
				route,
				duration: getDuration(startTime),
				statusCode: 0,
				responseData: undefined,
			}),
		)
	}

	/** Create request errors with only statusCode for mock purposes */
	public static forMock(statusCode: number) {
		return new RequestError('Mock request error', '', {
			statusCode,
			data: undefined,
			duration: 0,
			method: 'GET',
			responseData: undefined,
			route: '',
			url: '',
		})
	}
}
